import {
    ArrowDownward,
    ArrowUpward,
    CloseOutlined,
    FilterAltOffOutlined,
    FilterAltOutlined,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    Grid,
    IconButton,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { GET } from "../../../Utilities/BaseService";
import { MultiPermissionAuthorize } from "../../../Utilities/MultiPermissionAuthorize";
import UpdatedSearchableInput from "../../../Utilities/UpdatedSearchableInput";
import BaseTable from "../../Table/BaseTable";
import SearchBar from "../../Table/Components/SearchBar";
import { currencyFormatter } from "../Bill/Cells/Last12Summary";
import ArrearsReportDetail, {
    MonthlyDefaulterBreakdown,
} from "./ArrearsReportDetail";

type Filter = {
    search: string;
    district: number | "" | "all";
    village: number | "" | "all";
    hamlet: number | "" | "all";
    connectionType: number | "" | "all";
    startDate: any;
    endDate: any;
    powerstation: string;
    month: any;
    year: any;
    minMonths: any;
};

const initialFilter: Filter = {
    search: "",
    district: "",
    village: "",
    hamlet: "",
    startDate: "",
    endDate: "",
    connectionType: "",
    powerstation: "",
    month: "",
    year: "",
    minMonths: "",
};

const initialClearAll = {
    district: false,
    village: false,
    hamlet: false,
    powerstation: false,
    connectionType: false,
    search: false,
};

export const getArrearsReport = (pagination: any, filter: any) => {
    return GET("/reading/defaulters-report", {
        meterNo: filter.search ? filter.search : "",
        district:
            filter.district && filter.district !== "all"
                ? filter.district
                : undefined,
        village:
            filter.village && filter.village !== "all"
                ? filter.village
                : undefined,
        hamlet:
            filter.hamlet && filter.hamlet !== "all"
                ? filter.hamlet
                : undefined,
        startDate: filter.startDate ? filter.startDate : undefined,
        endDate: filter.endDate ? filter.endDate : undefined,
        connectionType: filter["connection type"],
        powerstation: filter.powerstation ? filter.powerstation : undefined,
        month: filter.month,
        year: filter.year,
        "connection type": undefined,
        minDefaulterMonths: filter.minMonths ? filter.minMonths : undefined,
        page: pagination.page + 1,
        limit: pagination.limit,
    });
};

const ArrearsReport = () => {
    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
    const [pagination, setPagination] = useState<any>({ page: 0, limit: 100 });
    const [billingMonth, setBillingMonth] = useState("");
    const [clearAll, setClearAll] = useState(initialClearAll);
    const [filter, setFilter] = useState<any>(initialFilter);
    const [feedback, setFeedback] = useState("");
    const [changeCount, setChangeCount] = useState(0);
    const [rowsCount, setRowsCount] = useState(0);
    const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>("desc"); // Sorting state
    const theme = useTheme();
    const mdDown = useMediaQuery(theme.breakpoints.down("md"));

    /* ================= CLEAR ALL ================= */
    const handleClearAll = () => {
        setFilter(initialFilter);
        setClearAll({
            district: true,
            village: true,
            powerstation: true,
            hamlet: true,
            search: true,
            connectionType: true,
        });
        setChangeCount(0);
        setPagination({ page: 0, limit: 100 });
        setBillingMonth("");
        setSortOrder(null); // Reset sorting

        setTimeout(
            () =>
                setClearAll({
                    district: false,
                    village: false,
                    powerstation: false,
                    hamlet: false,
                    search: false,
                    connectionType: false,
                }),
            0
        );
    };

    const { data, isLoading } = useQuery(
        ["arrears", pagination.page, pagination.limit, changeCount],
        () => getArrearsReport(pagination, filter),
        {
            enabled: changeCount > 0,
            onSuccess(res) {
                if (res) setRowsCount(res?.data.count);
            },
        }
    );

    const calcArrearsWithoutSurcharge = useMemo(
        () =>
            (row: MonthlyDefaulterBreakdown, formated = true) => {
                const { Total, Collection, PMCCollection } = row;
                const surcharge =
                    Total - ((Collection || 0) + (PMCCollection || 0));
                return formated
                    ? currencyFormatter.format(surcharge)
                    : surcharge;
            },
        [data?.data?.rows?.length]
    );

    const calcArrearsWithSurcharge = useMemo(
        () =>
            (row: MonthlyDefaulterBreakdown, formated = true) => {
                const {
                    Total,
                    AppliedSurcharge,
                    PaidSurcharge,
                    Collection,
                    PMCCollection,
                    AppliedSurchargeWhenNoCollection,
                    surchargeAdj,
                } = row;

                const appliedSurcharge = AppliedSurchargeWhenNoCollection ?? 0;

                console.log(
                    appliedSurcharge,
                    Total,
                    AppliedSurcharge,
                    PaidSurcharge,
                    Collection,
                    PMCCollection,
                    AppliedSurchargeWhenNoCollection,
                    surchargeAdj,
                    "data",
                    row.meterNo
                );
                const surcharge =
                    Total +
                    appliedSurcharge +
                    (AppliedSurcharge || 0) -
                    ((PaidSurcharge || 0) +
                        (surchargeAdj || 0) +
                        (Collection || 0) +
                        (PMCCollection || 0));

                return formated
                    ? currencyFormatter.format(surcharge)
                    : surcharge;
            },
        [data?.data?.rows?.length]
    );

    const createSummary = (cols: string[]) => {
        const summaryRow = ["Total"];
        if (!data?.data?.rows || !Array.isArray(data.data.rows))
            return summaryRow;

        let arrearsWithoutSurcharge = 0;
        let arrearsWithSurcharge = 0;

        for (const row of data?.data.rows) {
            arrearsWithoutSurcharge += calcArrearsWithoutSurcharge(
                row,
                false
            ) as number;
            arrearsWithSurcharge += calcArrearsWithSurcharge(
                row,
                false
            ) as number;
        }

        for (const col of cols) {
            switch (col) {
                case "arrearsWithoutSurcharge":
                    summaryRow.push(
                        currencyFormatter.format(arrearsWithoutSurcharge)
                    );
                    break;
                case "arrearsWithSurcharge":
                    summaryRow.push(
                        currencyFormatter.format(arrearsWithSurcharge)
                    );
                    break;
                default:
                    summaryRow.push("");
            }
        }
        return summaryRow;
    };

    // Sorted data based on arrearsWithSurcharge
    const sortedData = useMemo(() => {
        if (!data?.data?.rows) return [];

        let rows = [...data.data.rows];

        if (sortOrder) {
            rows.sort((a, b) => {
                const valA = calcArrearsWithSurcharge(a, false) as number;
                const valB = calcArrearsWithSurcharge(b, false) as number;
                return sortOrder === "asc" ? valA - valB : valB - valA;
            });
        }

        return rows.map((row: any) => ({
            ...row,
            details: <ArrearsReportDetail row={row} />,
            arrearsWithoutSurcharge: calcArrearsWithoutSurcharge(row),
            arrearsWithSurcharge: calcArrearsWithSurcharge(row),
            meterNo: `${row.meterNo}`,
            connectionType: row.connectionType,
        }));
    }, [data?.data?.rows, sortOrder]);

    useEffect(() => {
        if (
            filter.district &&
            filter.powerstation &&
            ![filter.district, filter.powerstation].includes("all")
        )
            return;

        setChangeCount(0);
    }, [filter.district, filter.powerstation]);

    useEffect(() => {
        if (billingMonth) {
            const [year, month] = billingMonth.split("-") ?? ["", ""];
            setFilter({ ...filter, year, month: parseInt(month) });
        }
    }, [billingMonth]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            {/* ================= FILTER CARD ================= */}
            <Card
                elevation={0}
                sx={{ bgcolor: theme.palette.common.white, p: 2, mb: "1rem" }}
            >
                <Grid container>
                    <Grid item xs={12} md="auto">
                        <Typography variant="h5" color="GrayText">
                            Arrears Report
                        </Typography>
                    </Grid>
                </Grid>

                <Grid container spacing={1} mt={1}>
                    {/* DISTRICT */}
                    <MultiPermissionAuthorize ops={["READ DISTRICT"]}>
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                api="/region/district"
                                label="District"
                                filter={filter}
                                setFilter={setFilter}
                                clearAll={clearAll.district}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    {/* POWERSTATION */}
                    <MultiPermissionAuthorize ops={["READ POWERSTATION"]}>
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                label="Powerstation"
                                api="/powerstation"
                                filter={filter}
                                setFilter={setFilter}
                                dep={filter.district}
                                params={{
                                    district:
                                        filter.district &&
                                        filter.district !== "all"
                                            ? filter.district
                                            : "",
                                }}
                                clearAll={clearAll.powerstation}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    {/* VILLAGE */}
                    <MultiPermissionAuthorize ops={["READ VILLAGE"]}>
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                api="/region/village"
                                label="Village"
                                filter={filter}
                                setFilter={setFilter}
                                dep={[filter.district, filter.powerstation]}
                                params={{
                                    district:
                                        filter.district &&
                                        filter.district !== "all"
                                            ? filter.district
                                            : "",
                                    powerstation:
                                        filter.powerstation &&
                                        filter.powerstation !== "all"
                                            ? filter.powerstation
                                            : "",
                                }}
                                clearAll={clearAll.village}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    {/* HAMLET */}
                    <MultiPermissionAuthorize ops={["READ HAMLET"]}>
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                api="/region/hamlet"
                                label="Hamlet"
                                filter={filter}
                                setFilter={setFilter}
                                dep={[filter.village, filter.powerstation]}
                                params={{
                                    village: filter.village,
                                    powerstation: filter.powerstation,
                                }}
                                clearAll={clearAll.hamlet}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    {/* CONNECTION TYPE */}
                    <MultiPermissionAuthorize ops={["READ CONNECTION_TYPE"]}>
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                key={filter.connectionType}
                                label="Connection Type"
                                api="/connection"
                                filter={filter}
                                setFilter={setFilter}
                                params={{ district: filter.district }}
                                multiple={true}
                                clearAll={clearAll.connectionType}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    {/* BILLING MONTH */}
                    <Grid item xs={12} md={6} lg={4}>
                        <TextField
                            size="small"
                            name="billingMonth"
                            label="Billing month"
                            type="month"
                            required
                            fullWidth
                            value={billingMonth}
                            onChange={(e) => setBillingMonth(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: currentMonth, min: "2025-10" }}
                            InputProps={{
                                endAdornment: billingMonth ? (
                                    <IconButton
                                        size="small"
                                        onClick={() => setBillingMonth("")}
                                    >
                                        <CloseOutlined />
                                    </IconButton>
                                ) : null,
                            }}
                        />
                    </Grid>

                    {/* MIN MONTHS */}
                    <Grid item xs={12} md={6} lg={4}>
                        <TextField
                            size="small"
                            name="minMonths"
                            label="Number of Defaulter Months"
                            type="number"
                            fullWidth
                            value={filter.minMonths || ""}
                            onChange={(e) =>
                                setFilter((f: any) => ({
                                    ...f,
                                    [e.target.name]:
                                        parseInt(e.target.value) <= 0
                                            ? ""
                                            : parseInt(e.target.value) || "",
                                }))
                            }
                        />
                    </Grid>

                    <Grid item xs={12} md={6} lg={4}>
                        <SearchBar
                            filter={filter}
                            setFilter={setFilter}
                            clear={clearAll.search}
                        />
                    </Grid>

                    <Grid item xs={12} md={6} lg={4}>
                        <Button
                            variant="outlined"
                            onClick={() =>
                                setSortOrder((prev) =>
                                    prev === "asc" ? "desc" : "asc"
                                )
                            }
                            color="secondary"
                            fullWidth
                            style={{ height: "100%" }}
                        >
                            {sortOrder === "asc"
                                ? "Ascending Order"
                                : "Descending Order"}

                            {sortOrder === "asc" ? (
                                <ArrowUpward fontSize="small" />
                            ) : sortOrder === "desc" ? (
                                <ArrowDownward fontSize="small" />
                            ) : (
                                ""
                            )}
                        </Button>
                    </Grid>

                    {/* SEARCH */}
                    <Grid item xs={12} md={6} lg={2}>
                        <Button
                            color="secondary"
                            variant="outlined"
                            onClick={() => {
                                setChangeCount((n) =>
                                    filter.search
                                        ? n + 1
                                        : [
                                                filter.district,
                                                filter.powerstation,
                                            ].some(
                                                (x) => x === "all" || x === ""
                                            )
                                          ? 0
                                          : n + 1
                                );
                            }}
                            sx={{ height: "100%" }}
                            startIcon={<FilterAltOutlined />}
                            fullWidth
                        >
                            Search
                        </Button>
                    </Grid>

                    {/* CLEAR ALL */}
                    <Grid item xs={12} md={6} lg={2}>
                        <Button
                            color="error"
                            variant="outlined"
                            startIcon={<FilterAltOffOutlined />}
                            onClick={handleClearAll}
                            sx={{ height: "100%", minWidth: theme.spacing(20) }}
                            fullWidth
                        >
                            Clear All
                        </Button>
                    </Grid>

                    {/* SORT BUTTON */}
                </Grid>
            </Card>

            {/* ================= TABLE ================= */}
            <Box>
                {changeCount === 0 ? (
                    <Alert severity="info" sx={{ mt: "1rem" }}>
                        Please select "District" and "PowerStation" or enter
                        "Meter No"
                    </Alert>
                ) : (
                    <BaseTable
                        headers={{
                            meterId: "ID",
                            customerName: "Name",
                            fatherName: "fatherName",
                            cnic: "Cnic",
                            mobileNumber: "mobileNumber",
                            meterNo: "meterNo",
                            districtName: "District",
                            districtId: "districtId",
                            powerstationName: "PowerStation",
                            villageId: "villageId",
                            villageName: "Village",
                            hamletName: "Hamlet",
                            hamletId: "hamletId",
                            defaulterMonths: "Months",
                            arrearsWithoutSurcharge:
                                "Arrears Without Surcharge",
                            arrearsWithSurcharge: "Arrears With Surcharge",
                            connectionType: "connectionType",
                            details: "Details",
                        }}
                        defaultSelectedHeaders={[
                            "customerName",
                            "fatherName",
                            "cnic",
                            "mobileNumber",
                            "meterNo",
                            "districtName",
                            "powerstationName",
                            "villageName",
                            "hamletName",
                            "defaulterMonths",
                            "arrearsWithoutSurcharge",
                            "arrearsWithSurcharge",
                            "details",
                        ]}
                        data={sortedData} // ✅ sorted data
                        delEndPoint=""
                        feedback={feedback}
                        setFeedback={setFeedback}
                        rowsCount={rowsCount}
                        pagination={pagination}
                        setPagination={setPagination}
                        isLoading={isLoading}
                        permissions={{ edit: [""], delete: [""] }}
                        load={
                            (filter.search
                                ? filter.search !== ""
                                : Boolean(filter.district || filter.search) &&
                                  filter.district !== "all") && changeCount > 0
                        }
                        queryKey={[
                            "arrears",
                            pagination.page,
                            pagination.limit,
                            changeCount,
                        ]}
                        reportName="Arrears__List"
                        reportDetails={{
                            district: filter.district || undefined,
                            powerstation: filter.powerstation || undefined,
                            village: filter.village || undefined,
                            hamlet: filter.hamlet || undefined,
                        }}
                        summaryRow={createSummary}
                    />
                )}
            </Box>
        </Box>
    );
};

export default ArrearsReport;
